import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Container
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Science as ChemoIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getChemotherapyRegimens } from '../../services/chemotherapyService';
import { PageHeader, HeaderButton, PageContainer, ContentCard } from './oncologyStyles';

const ChemotherapyRegimens = ({ onSelectRegimen, cancerTypeFilter, embedded = false }) => {
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regimens, setRegimens] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegimen, setSelectedRegimen] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const loadRegimens = useCallback(async () => {
    if (!defaultFacility?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await getChemotherapyRegimens(defaultFacility.id, {
        cancerType: cancerTypeFilter,
        isActive: true
      });
      setRegimens(result.data || []);
    } catch (err) {
      console.error('Error loading chemotherapy regimens:', err);
      setError('Failed to load chemotherapy regimens');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id, cancerTypeFilter]);

  useEffect(() => {
    loadRegimens();
  }, [loadRegimens]);

  const filteredRegimens = regimens.filter(regimen =>
    regimen.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    regimen.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewDetails = (regimen) => {
    setSelectedRegimen(regimen);
    setDetailsDialogOpen(true);
  };

  const handleSelectRegimen = (regimen) => {
    if (onSelectRegimen) {
      onSelectRegimen(regimen);
    }
  };

  const content = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search regimens..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{ mb: 3 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredRegimens.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No chemotherapy regimens found.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredRegimens.map((regimen) => (
            <Grid item xs={12} md={6} lg={4} key={regimen.id}>
              <Card variant="outlined" sx={{ borderRadius: '12px' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {regimen.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {regimen.code}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {regimen.cancerTypes?.slice(0, 3).map((type) => (
                      <Chip key={type} label={type} size="small" variant="outlined" />
                    ))}
                    {regimen.cancerTypes?.length > 3 && (
                      <Chip label={`+${regimen.cancerTypes.length - 3}`} size="small" />
                    )}
                  </Box>
                  <Typography variant="body2">
                    <strong>Cycle:</strong> {regimen.cycleLengthDays} days × {regimen.numberOfCycles} cycles
                  </Typography>
                  <Typography variant="body2">
                    <strong>Drugs:</strong> {regimen.drugs?.length || 0}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => handleViewDetails(regimen)}>
                    Details
                  </Button>
                  {onSelectRegimen && (
                    <Button size="small" variant="contained" onClick={() => handleSelectRegimen(regimen)}>
                      Select
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChemoIcon color="primary" />
            {selectedRegimen?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRegimen && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {selectedRegimen.code}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Cycle Length:</strong> {selectedRegimen.cycleLengthDays} days</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Number of Cycles:</strong> {selectedRegimen.numberOfCycles}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Drugs in Regimen
              </Typography>
              <List dense>
                {selectedRegimen.drugs?.map((drug, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={drug.drugName}
                      secondary={`${drug.standardDose} ${drug.doseUnit} - ${drug.route} - Days ${drug.scheduleDays?.join(', ')}`}
                    />
                  </ListItem>
                ))}
              </List>

              {selectedRegimen.preChemoLabs?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Pre-Chemotherapy Labs
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedRegimen.preChemoLabs.map((lab, index) => (
                      <Chip key={index} label={lab} size="small" variant="outlined" />
                    ))}
                  </Box>
                </>
              )}

              {selectedRegimen.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body2">{selectedRegimen.notes}</Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {onSelectRegimen && (
            <Button onClick={() => { setDetailsDialogOpen(false); handleSelectRegimen(selectedRegimen); }} variant="contained">
              Select Regimen
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );

  // Embedded mode (inside another component)
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChemoIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Chemotherapy Regimens
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              size="small"
              onClick={() => navigate('/oncology/chemotherapy/new')}
            >
              Add Custom Regimen
            </Button>
          </Box>
          {content}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page mode
  return (
    <PageContainer>
      <Container maxWidth="lg">
        {/* Header with Back Navigation */}
        <PageHeader
          title="Chemotherapy Management"
          subtitle="Protocol management, cycle tracking, and dose calculations"
          icon={<ChemoIcon />}
          backTo="/oncology"
          actions={
            <HeaderButton 
              startIcon={<AddIcon />}
              onClick={() => navigate('/oncology/chemotherapy/new')}
            >
              Add Custom Regimen
            </HeaderButton>
          }
        />

        <ContentCard>
          <CardContent>
            {content}
          </CardContent>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

export default ChemotherapyRegimens;
